import {DocumentNode, gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingAppDataAccessTab from 'benchling-apps/BenchlingAppDataAccessTab';
import BenchlingAppConfigurationSection from 'benchling-apps/configuration/BenchlingAppConfigurationSection';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Tab, TabContainer} from 'ui-library/TabContainer';

import {
  getBenchlingAppDetailsQuery,
  getBenchlingAppDetailsQueryVariables,
} from './__generated/getBenchlingAppDetailsQuery';
import BenchlingAppSessionActivityTable from './activity/BenchlingAppSessionActivityTable';
import BenchlingAppDetailsGeneral, {BenchlingAppDetailsStatus} from './BenchlingAppDetailsGeneral';

const BENCHLING_APP_DETAILS_GENERAL_FRAGMENT: DocumentNode = gql`
  fragment BenchlingAppDetailsGeneralBenchlingApp on BenchlingAppNode {
    id
    archived
    name
    description
    lastSeen
    lifecycleState
    creator {
      id
      handle
      name
      avatar_url: avatarUrl
    }
    webhookConfigurations {
      ... on InaccessibleInfoNode {
        invalidId
      }
      ... on WebhookConfigurationNode {
        id
      }
    }
    versionNumber
    appDefinitionId
    subscriptions
  }
`;

export const BENCHLING_APP_DETAILS_QUERY = gql`
  query getBenchlingAppDetailsQuery($benchlingAppId: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $benchlingAppId) {
      ... on BenchlingAppNode {
        id
        ...BenchlingAppDetailsGeneralBenchlingApp
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${BENCHLING_APP_DETAILS_GENERAL_FRAGMENT}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// AVAILABLE TABS
const BENCHLING_APP_DETAILS_OVERVIEW = 'BENCHLING_APP_DETAILS_OVERVIEW';
const BENCHLING_APP_DETAILS_CONFIGURATION = 'BENCHLING_APP_DETAILS_CONFIGURATION';
const BENCHLING_APP_DETAILS_ACCESS = 'BENCHLING_APP_DETAILS_ACCESS';
const BENCHLING_APP_DETAILS_ACTIVITY = 'BENCHLING_APP_DETAILS_ACTIVITY';

export default function BenchlingAppDetails({benchlingAppId}: {benchlingAppId: string}): JSX.Element {
  const [currentTab, onCurrentTabChange] = useState<string>(BENCHLING_APP_DETAILS_OVERVIEW);
  const queryResult = useBenchlingQuery<getBenchlingAppDetailsQuery, getBenchlingAppDetailsQueryVariables>(
    BENCHLING_APP_DETAILS_QUERY,
    {
      variables: {benchlingAppId},
    }
  );
  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    notifications.error('Unable to load app. Please try again.');
    return <></>;
  }
  if (isInaccessible(queryResult.data.benchlingApp)) {
    return (
      <WorkspaceItemInaccessibleGuard
        inaccessibleInfo={queryResult.data.benchlingApp}
        modelType={ModelTypeConstants.BENCHLING_APP}
      />
    );
  }

  const benchlingApp = queryResult.data.benchlingApp;

  return (
    <div
      className={css(styles.pageContainer)}
      {...getTestProps({component: 'BenchlingAppDetails', element: 'root'})}
    >
      <BenchlingAppDetailsStatus {...benchlingApp} />
      <TabContainer
        className={css(styles.tabContainer)}
        activeTab={currentTab}
        navClassName={classNames(css(styles.stickyNav))}
        paneClassName={classNames('u-flex-grow', 'scrollbar-override', css(styles.container))}
        onRequestTabChange={onCurrentTabChange}
      >
        <Tab tabKey={BENCHLING_APP_DETAILS_OVERVIEW} title="GENERAL">
          <div className={css(styles.contentCard)}>
            <BenchlingAppDetailsGeneral
              {...benchlingApp}
              refetchResults={async () => {
                await queryResult.refetch();
              }}
            />
          </div>
        </Tab>
        <Tab tabKey={BENCHLING_APP_DETAILS_CONFIGURATION} title="CONFIGURATION">
          <div
            className={css(styles.contentCard)}
            {...getTestProps({component: 'BenchlingAppConfigurationSection', element: 'root'})}
          >
            <BenchlingAppConfigurationSection benchlingAppId={benchlingAppId} />
          </div>
        </Tab>
        <Tab tabKey={BENCHLING_APP_DETAILS_ACCESS} title="ACCESS">
          <div className={css(styles.content)}>
            <BenchlingAppDataAccessTab appId={benchlingAppId} />
          </div>
        </Tab>
        <Tab tabKey={BENCHLING_APP_DETAILS_ACTIVITY} title="ACTIVITY">
          <div className={css(styles.contentCard)}>
            <BenchlingAppSessionActivityTable appId={benchlingAppId} />
          </div>
        </Tab>
      </TabContainer>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    height: 'calc(100% - 50px)',
  },
  tabContainer: {
    height: '100%',
  },
  container: {
    height: 'calc(100% - 37px)',
    overflow: 'auto',
    marginTop: '0px',
  },
  contentCard: {
    margin: '24px auto',
    maxWidth: '840px',
    backgroundColor: `${Colors.WHITE}`,
    borderRadius: '3px',
  },
  content: {
    margin: '0px',
    height: '100%',
  },
  stickyNav: {
    position: 'sticky',
    zIndex: 1,
    top: '50px',
    paddingBottom: '0px',
    backgroundColor: `${Colors.WHITE}`,
    height: '36px',
    marginBottom: '0px',
    padding: '10px 12px 6px 14px',
  },
  appTitle: {
    padding: '14px 12px 12px 14px',
    backgroundColor: `${Colors.WHITE}`,
    marginBottom: '0px',
    position: 'sticky',
    zIndex: 1,
    top: 0,
  },
});
